/* eslint-disable indent */
import { Checkbox, FormControlLabel, Grid } from '@mui/material';
import { getIn } from 'formik';
import { DateTime } from 'luxon';
import PropTypes from 'prop-types';
import React, { memo, useCallback, useMemo } from 'react';

import { DollarIcon } from 'assets/v2/icons';
import Calendar from 'components/v2/Calendar';
import InputField, { SIZES } from 'components/v2/InputField';
import { zoneIgnoredDate } from 'utils/helpers';
import useFormFieldValidator from 'utils/hooks/useFormFieldValidator';
import { getTimezoneLocalISO } from 'utils/v2';

/**
 * TripLodgingsForm
 *
 * A reusable component for managing lodgings-related fields in trip forms.
 *
 * Handles check-in/check-out dates, number of reserved rooms, lodging cost, and advanced payment checkbox.
 *
 * @param {Object} props
 * @param {Object} props.formik - Formik instance
 * @param {string} props.fieldPrefix - Dot-notation path for lodgings, e.g. 'ancillaryServices.lodgings' or 'itineraryForm.legs[0].lodgings'
 * @param {boolean} props.isLoading - Disables input fields while loading
 * @param {string} props.timezone - Timezone string for date conversion
 * @param {Object} [props.schema] - Optional Yup schema for required field validation
 * @param {string} [props.lodgingsAmountTitle='Cost of Room Per Day'] - Label for lodging cost input
 * @param {boolean} [props.isRequired] - Whether the fields are required; controls validation
 */
const TripLodgingsForm = ({
  formik,
  fieldPrefix,
  isLoading,
  timezone,
  schema,
  lodgingsAmountTitle = 'Cost of Room Per Day',
  isRequired,
}) => {
  const { values, setFieldValue, setFieldTouched } = formik;

  const requiredFields = useMemo(
    () =>
      isRequired
        ? [
            `${fieldPrefix}.checkInDateTime`,
            `${fieldPrefix}.checkOutDateTime`,
            `${fieldPrefix}.numberOfReservedUnits`,
            `${fieldPrefix}.lodgingCostInCents`,
          ]
        : [],
    [fieldPrefix, isRequired],
  );

  const fieldGroups = useMemo(
    () => [
      [
        `${fieldPrefix}.checkInDateTime`,
        `${fieldPrefix}.checkOutDateTime`,
        `${fieldPrefix}.numberOfReservedUnits`,
        `${fieldPrefix}.advancedPaymentRequired`,
        `${fieldPrefix}.lodgingCostInCents`,
      ],
    ],
    [fieldPrefix],
  );

  const { fieldErrorMsg, fieldValidation, setUpdatedField } =
    useFormFieldValidator({
      formik,
      schema,
      requiredFields,
      fieldGroups,
    });

  const setValue = useCallback(
    async (key, value) => {
      const fullKey = `${fieldPrefix}.${key}`;
      await setFieldValue(fullKey, value);
      setFieldTouched(fullKey);
      setUpdatedField(fullKey);
    },
    [fieldPrefix, setFieldValue, setFieldTouched, setUpdatedField],
  );

  const handleCheckInChange = useCallback(
    (date) =>
      setValue(
        'checkInDateTime',
        date?.setZone?.(timezone, { keepLocalTime: true })?.toUTC()?.toISO() ||
          date.toString(),
      ),
    [setValue, timezone],
  );

  const handleCheckoutChange = useCallback(
    (date) =>
      setValue(
        'checkOutDateTime',
        date?.setZone?.(timezone, { keepLocalTime: true })?.toUTC()?.toISO() ||
          date.toString(),
      ),
    [setValue, timezone],
  );

  const handleReservedChange = useCallback(
    ({ target: { value } }) =>
      setValue(
        'numberOfReservedUnits',
        value === '' ? '' : parseInt(value, 10).toString(),
      ),
    [setValue],
  );

  const handleAmountChange = useCallback(
    ({ target: { value } }) => {
      if (value === '') {
        setValue('lodgingCostInCents', '');
      } else {
        setValue(
          'lodgingCostInCents',
          Math.round(parseFloat(value) * 100).toString(),
        );
      }
    },
    [setValue],
  );

  const handlePaymentChange = useCallback(
    ({ target: { checked } }) => setValue('advancedPaymentRequired', checked),
    [setValue],
  );

  const checkInDate = useMemo(() => {
    const checkInDateFormValue = getIn(
      values,
      `${fieldPrefix}.checkInDateTime`,
    );
    return (
      getTimezoneLocalISO({
        isoDate: checkInDateFormValue,
        timezone,
      }) ?? checkInDateFormValue
    );
  }, [values, fieldPrefix, timezone]);

  const checkoutDate = useMemo(() => {
    const checkOutDateFormValue = getIn(
      values,
      `${fieldPrefix}.checkOutDateTime`,
    );
    return (
      getTimezoneLocalISO({
        isoDate: checkOutDateFormValue,
        timezone,
      }) ?? checkOutDateFormValue
    );
  }, [values, fieldPrefix, timezone]);

  const checkOutMinDate = useMemo(() => {
    const checkInDateFormValue = getIn(
      values,
      `${fieldPrefix}.checkInDateTime`,
    );
    return checkInDateFormValue &&
      DateTime.fromISO(checkInDateFormValue).isValid
      ? zoneIgnoredDate(checkInDateFormValue)
      : undefined;
  }, [values, fieldPrefix]);

  const roomCount = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.numberOfReservedUnits`);
    if (!formValue && formValue !== 0) return '';
    return formValue;
  }, [values, fieldPrefix]);

  const lodgingAmount = useMemo(() => {
    const formValue = getIn(values, `${fieldPrefix}.lodgingCostInCents`);
    if (formValue !== 0 && !formValue) return '';
    return formValue / 100;
  }, [values, fieldPrefix]);

  const advancedPayment = useMemo(
    () => getIn(values, `${fieldPrefix}.advancedPaymentRequired`) || false,
    [values, fieldPrefix],
  );

  const checkInRenderInput = useCallback(
    (params) => (
      <InputField
        {...params}
        required={isRequired}
        size={SIZES.MEDIUM}
        valid={fieldValidation[`${fieldPrefix}.checkInDateTime`]}
        error={fieldErrorMsg[`${fieldPrefix}.checkInDateTime`]}
      />
    ),
    [fieldValidation, fieldErrorMsg, fieldPrefix, isRequired],
  );

  const checkoutRenderInput = useCallback(
    (params) => (
      <InputField
        {...params}
        required={isRequired}
        size={SIZES.MEDIUM}
        valid={fieldValidation[`${fieldPrefix}.checkOutDateTime`]}
        error={fieldErrorMsg[`${fieldPrefix}.checkOutDateTime`]}
      />
    ),
    [fieldValidation, fieldErrorMsg, fieldPrefix, isRequired],
  );

  return (
    <Grid container spacing={3}>
      <Grid item xs={12} sm={6}>
        <Calendar
          inputFormat="MM/dd/yyyy"
          label="Check-In Date"
          disabled={isLoading}
          value={checkInDate}
          onChange={handleCheckInChange}
          renderInput={checkInRenderInput}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <Calendar
          inputFormat="MM/dd/yyyy"
          label="Check-Out Date"
          disabled={isLoading}
          value={checkoutDate}
          onChange={handleCheckoutChange}
          minDate={checkOutMinDate}
          renderInput={checkoutRenderInput}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputField
          type="number"
          autoComplete="off"
          label="Number of Rooms"
          disabled={isLoading}
          required={isRequired}
          value={roomCount}
          onChange={handleReservedChange}
          valid={fieldValidation[`${fieldPrefix}.numberOfReservedUnits`]}
          error={fieldErrorMsg[`${fieldPrefix}.numberOfReservedUnits`]}
        />
      </Grid>
      <Grid item xs={12} sm={6}>
        <InputField
          type="number"
          autoComplete="off"
          label={lodgingsAmountTitle}
          adornment={<DollarIcon />}
          min={0}
          disabled={isLoading}
          required={isRequired}
          value={lodgingAmount}
          onChange={handleAmountChange}
          valid={fieldValidation[`${fieldPrefix}.lodgingCostInCents`]}
          error={fieldErrorMsg[`${fieldPrefix}.lodgingCostInCents`]}
        />
      </Grid>
      <Grid item xs={12}>
        <FormControlLabel
          checked={advancedPayment}
          control={<Checkbox />}
          disabled={isLoading}
          label="Advanced"
          sx={{ mt: -1, ml: -1 }}
          onChange={handlePaymentChange}
        />
      </Grid>
    </Grid>
  );
};

TripLodgingsForm.propTypes = {
  formik: PropTypes.object.isRequired,
  fieldPrefix: PropTypes.string.isRequired,
  isLoading: PropTypes.bool,
  timezone: PropTypes.string,
  schema: PropTypes.object,
  lodgingsAmountTitle: PropTypes.string,
  isRequired: PropTypes.bool,
};

export default memo(TripLodgingsForm);
